// Everything mounted on one surface, in that surface's own (u, v).
//
// The snap resolver used to be fed from `doc.panels` alone, which is why
// dragging a panel around a wall-mounted TV felt like the TV was not there:
// it is not a panel, so it contributed no edge to measure against, and the
// same went for a window and for a projector's screen. Those are exactly the
// things a user positions treatment AROUND, so the omission was the whole bug
// rather than a missing nicety.
//
// Collected here rather than in the drag hook so mobile and any future 2D
// editor get the same answer, and so the awkward parts — which entries are
// even ON a surface, which are stored in (u, v) already and which need
// projecting out of a world transform — are stated once.

import type { EditorDoc, MediaEntry, WindowEntry } from '@roomtreatment/core';
import { mediaAsMountable, screenSize, windowPolygon } from '@roomtreatment/core';
import {
  halfExtents, surfaceKey, transformToPose, windowsOn,
  type SurfaceFrame,
} from './panelSurface';
import { doorsOn } from './doorOpening';

/** What a footprint came from. Carried so a guide can name the thing it
 *  measured against, and so a caller can weight kinds differently later
 *  without re-deriving any geometry. */
export type SurfaceFootprintKind = 'panel' | 'media' | 'screen' | 'window' | 'door';

/** One mounted object's box on a surface, in that surface's own (u, v). */
export interface SurfaceFootprint {
  objectId: string;
  kind: SurfaceFootprintKind;
  u: number;
  v: number;
  hu: number;
  hv: number;
}

/** How far off a surface an entry may sit and still count as mounted on it,
 *  metres. Inherited from the drag hook's original gate. Generous enough for
 *  a legacy free-floating panel that predates surface glue, tight enough that
 *  a panel on the facing wall — metres away along the normal — never
 *  qualifies. */
const ON_SURFACE_TOLERANCE_M = 0.05;

/** A world-transformed entry's footprint on `frame`, or null if it is not on
 *  this surface at all. The shared path for panels and TVs: both store a
 *  world transform and derive their surface from it, so both project the same
 *  way (`mediaAsMountable` is the adapter the rest of the codebase already
 *  uses for exactly this). */
function mountedFootprint(
  frame: SurfaceFrame,
  objectId: string,
  kind: SurfaceFootprintKind,
  position: readonly [number, number, number],
  rotation: readonly [number, number, number],
  size: { w: number; h: number; d: number },
): SurfaceFootprint | null {
  const { pose, offset } = transformToPose(
    frame, [position[0], position[1], position[2]], [rotation[0], rotation[1], rotation[2]], size,
  );
  if (Math.abs(offset) > ON_SURFACE_TOLERANCE_M) return null;
  const { hu, hv } = halfExtents(size, pose.spin);
  return { objectId, kind, u: pose.u, v: pose.v, hu, hv };
}

/** An opening's half-extents. A window with a `polygon` (an arch, a trapezoid)
 *  can reach outside the `w × h` box its scalar fields describe, so the
 *  outline wins wherever there is one — snapping to a stated width that the
 *  glass visibly overshoots would be worse than not snapping. */
function windowHalfExtents(win: WindowEntry): { u: number; v: number; hu: number; hv: number } {
  // `windowPolygon` already falls back to the rectangle from u/v/w/h when
  // there is no outline, so this one path covers both shapes.
  let minU = Infinity; let maxU = -Infinity;
  let minV = Infinity; let maxV = -Infinity;
  for (const p of windowPolygon(win)) {
    if (p.u < minU) minU = p.u;
    if (p.u > maxU) maxU = p.u;
    if (p.v < minV) minV = p.v;
    if (p.v > maxV) maxV = p.v;
  }
  if (!Number.isFinite(minU) || !Number.isFinite(minV)) {
    return { u: win.u, v: win.v, hu: win.w / 2, hv: win.h / 2 };
  }
  return {
    u: (minU + maxU) / 2,
    v: (minV + maxV) / 2,
    hu: (maxU - minU) / 2,
    hv: (maxV - minV) / 2,
  };
}

/** A projector's screen, if it is thrown at `frame`. The screen is stored in
 *  the host wall's (u, v) already — no projection needed — and is a real
 *  rectangle on that wall whether or not the projector body is anywhere near
 *  it. Keyed by the OWNING projector's id, since the screen has none of its
 *  own and the guide should name the device a user can select. */
function screenFootprint(frame: SurfaceFrame, entry: MediaEntry): SurfaceFootprint | null {
  const screen = entry.screen;
  if (!screen || screen.surface !== surfaceKey(frame.ref)) return null;
  const size = screenSize(entry);
  return {
    objectId: entry.id,
    kind: 'screen',
    u: screen.u,
    v: screen.v,
    hu: size.w / 2,
    hv: size.h / 2,
  };
}

/**
 * Every object mounted on `frame`, excluding `excludeId` (the thing being
 * dragged — it must never measure against itself, or the snap locks it to its
 * own current position and the drag appears frozen).
 *
 * Deliberately NOT included:
 *   • corner traps — their footprint on a wall is a wedge, not a box, and
 *     `clampPose`'s `cornerClearance` already keeps panels out of them with
 *     the real triangular geometry rather than a box approximation.
 *   • detached entries — a TV on a stand is a free body in the room, not
 *     something on this wall.
 *   • a projector BODY — it hangs from the ceiling and is yawed to throw at a
 *     screen, so `inferSurface` resolves it to the wall it points at, which
 *     is the one surface it is definitely not attached to. Its SCREEN is
 *     collected instead, which is the thing actually on the wall.
 */
export function surfaceFootprints(
  doc: EditorDoc, frame: SurfaceFrame, excludeId?: string,
): SurfaceFootprint[] {
  if (frame.ref.kind === 'corner') return [];
  const out: SurfaceFootprint[] = [];

  for (const p of doc.panels) {
    if (p.id === excludeId || p.detached || p.shape === 'corner') continue;
    const f = mountedFootprint(frame, p.id, 'panel', p.position, p.rotation, p.size);
    if (f) out.push(f);
  }

  for (const m of doc.media ?? []) {
    if (m.id === excludeId) continue;
    if (!m.detached && m.kind !== 'projector') {
      const mountable = mediaAsMountable(m);
      const f = mountedFootprint(frame, m.id, 'media', m.position, m.rotation, mountable.size);
      if (f) out.push(f);
    }
    const screen = screenFootprint(frame, m);
    if (screen) out.push(screen);
  }

  for (const w of windowsOn(doc, frame.ref)) {
    if (w.id === excludeId) continue;
    const { u, v, hu, hv } = windowHalfExtents(w);
    out.push({ objectId: w.id, kind: 'window', u, v, hu, hv });
  }

  // Doors are always a plain rectangle (no polygon), but `windowHalfExtents`
  // handles that case too — a `DoorEntry` is structurally a `WindowEntry`.
  for (const d of doorsOn(doc, frame.ref)) {
    if (d.id === excludeId) continue;
    const { u, v, hu, hv } = windowHalfExtents(d);
    out.push({ objectId: d.id, kind: 'door', u, v, hu, hv });
  }

  return out;
}
